/**
 * The one audience vocabulary behind `presence_consent` and `invites_from`.
 * Declared here rather than in `social-client` because `GET /auth/me` is what
 * hands these values to the browser — `social-client` re-exports this type.
 */
export type PrivacyScope = 'everyone' | 'friends' | 'nobody';

export interface Profile {
  userId: string;
  handle: string | null;
  displayName: string;
  avatarUrl: string;
  presenceConsent: PrivacyScope;
  invitesFrom: PrivacyScope;
}

const MAX_NAME_LENGTH = 18;

// An empty <img src> re-requests the current page per the HTML spec — every
// consumer must omit the attribute rather than bind an empty string when a
// provider gave no avatar picture.
export function avatarSrc(avatarUrl: string): string | undefined {
  return avatarUrl !== '' ? avatarUrl : undefined;
}

// Array.from splits by Unicode code point rather than UTF-16 code unit, so a
// surrogate pair (e.g. an emoji in a display name) is never cut in half.
export function truncateName(name: string): string {
  const chars = Array.from(name);
  if (chars.length <= MAX_NAME_LENGTH) return name;
  return chars.slice(0, MAX_NAME_LENGTH).join('') + '…';
}

export type AuthState =
  | { kind: 'unauthenticated' }
  | { kind: 'incomplete'; profile: Profile }
  | { kind: 'authenticated'; profile: Profile & { handle: string } };

// The auth-state branching every UI surface needs (AuthWidget, landing's
// header) — shared so the profile.handle narrowing lives in one place rather
// than being copy-pasted per consumer.
export function classifyAuthState(profile: Profile | null): AuthState {
  if (profile === null) {
    return { kind: 'unauthenticated' };
  }
  const { handle } = profile;
  if (handle === null) {
    return { kind: 'incomplete', profile };
  }
  return { kind: 'authenticated', profile: { ...profile, handle } };
}

